import { Stack, Typography } from '@mui/material'
import { useTheme } from '@emotion/react'
import { waitBoxLabel } from './utils/waitBoxLabel'

/**
 * A wait box on the Gantt: the striped background it always had, plus the label that says WHICH
 * work is waiting -- the fabrication order code and the job the wait precedes (RN39/D10 of story
 * #146). Until now the box was stripes and nothing else, so a row full of waits said that something
 * was queued but never what.
 *
 * The text is one line and clips with an ellipsis: the box keeps the fixed height the calendar lays
 * out for it (3rem), so a long label may not push it taller. The full detail -- order, job,
 * technology and dates -- is one click away, in the side panel.
 *
 * @param {Object} props Props object
 * @param {Object} props.event The FullCalendar event of the wait
 * @returns {JSX.Element} The box
 */
const CalendarWaitItem = ({ event }) => {
  const theme = useTheme()

  const bgStyle = {

    '--s': '1.2rem', // stripe thickness
    '--c1': theme.palette.warning.main, // pale
    '--c2': theme.palette.warning.dark, // accent

    background: `
    repeating-linear-gradient(
      to bottom,
      var(--c1) 0 calc(var(--s)/2),
      var(--c2) calc(var(--s)/2) var(--s)
    )
  `,
    backgroundSize: '100% var(--s)' // full width, repeat vertically,

  }

  // The first wait is THE wait: the contract sends exactly one element per box in the groupings
  // that have this row. Older plans and other groupings may send none or several, and neither may
  // break the box -- it draws the first label it has, or none at all.
  const label = waitBoxLabel(event?.extendedProps?.waits?.[0])

  return (
    <Stack
      data-testid='wait-box'
      justifyContent='center'
      sx={{ height: '3rem', backgroundColor: 'red', overflow: 'hidden', ...bgStyle }}
    >
      {label === ''
        ? null
        : (
          <Typography
            data-testid='wait-box-label'
            noWrap
            title={label}
            sx={{
              px: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: theme.palette.getContrastText(theme.palette.warning.main)
            }}
          >
            {label}
          </Typography>
          )}
    </Stack>

  )
}

export default CalendarWaitItem
